/**
 * Partition-extraction dry-run. Read-only preview of what an
 * `extractPartition` would move: record counts, byte totals, and the
 * timestamp span per collection — computed from raw encrypted
 * envelopes WITHOUT decrypting them. Writes nothing, mutates nothing.
 *
 * @module
 */
import type { Vault } from '../kernel/vault.js'
import { walkClosure, type WalkClosureOptions, type DanglingRefNotice } from './walk-closure.js'

export interface ExtractionPreview {
  readonly totalRecords: number
  /** Sum of serialized encrypted-envelope sizes (bytes). */
  readonly totalBytes: number
  readonly byCollection: ReadonlyArray<{
    readonly name: string
    readonly recordCount: number
    readonly bytes: number
    /** Earliest envelope `_ts` in this collection (lexicographic). */
    readonly oldestTs?: string
    readonly newestTs?: string
  }>
  readonly graph: { readonly depth: number; readonly cyclesDetected: boolean }
  /** Records the walk reached but whose envelope couldn't be read. */
  readonly inaccessible: ReadonlyArray<{ readonly collection: string; readonly id: string }>
  /**
   * #772: outbound FK edges whose referenced parent was excluded from the
   * closure (missing, or tier-elevated and therefore invisible) — mirrors
   * `ExtractPartitionResult.danglingRefs` (#759) so a preview/extract pair
   * gives the SAME signal. A parent excluded this way never appears in
   * `byCollection` at all; this is the only preview-time indicator.
   */
  readonly danglingRefs: ReadonlyArray<DanglingRefNotice>
}

export async function describeExtraction(
  vault: Vault,
  opts: WalkClosureOptions,
): Promise<ExtractionPreview> {
  const { closure, graph, danglingRefs } = await walkClosure(vault, opts)

  const { name: vaultName, adapter } = vault._introspectState()
  const encoder = new TextEncoder()

  const byCollection: Array<{
    name: string; recordCount: number; bytes: number; oldestTs?: string; newestTs?: string
  }> = []
  const inaccessible: Array<{ collection: string; id: string }> = []
  let totalBytes = 0
  let totalRecords = 0

  for (const [collectionName, ids] of closure) {
    let bytes = 0
    let oldestTs: string | undefined
    let newestTs: string | undefined
    let recordCount = 0

    for (const id of ids) {
      const env = await adapter.get(vaultName, collectionName, id)
      if (!env) {
        // Walk reached it (via decrypted list) but the raw store read
        // returned nothing — surface rather than miscount.
        inaccessible.push({ collection: collectionName, id })
        continue
      }
      recordCount++
      bytes += encoder.encode(JSON.stringify(env)).length
      const ts = env._ts
      if (oldestTs === undefined || ts < oldestTs) oldestTs = ts
      if (newestTs === undefined || ts > newestTs) newestTs = ts
    }

    byCollection.push({
      name: collectionName,
      recordCount,
      bytes,
      // Spread conditionally — exactOptionalPropertyTypes forbids an
      // explicit `undefined` on an optional property.
      ...(oldestTs !== undefined ? { oldestTs } : {}),
      ...(newestTs !== undefined ? { newestTs } : {}),
    })
    totalBytes += bytes
    totalRecords += recordCount
  }

  byCollection.sort((a, b) => a.name.localeCompare(b.name))

  return Object.freeze({
    totalRecords,
    totalBytes,
    byCollection,
    graph,
    inaccessible,
    danglingRefs,
  })
}
